'use client';

// Client boundary is required: <Alert> is a client component and `icon={Bot}`
// passes a component reference, which cannot be serialized from a server
// component ("Functions cannot be passed directly to Client Components").
// Same pattern as context-clue.tsx and key-takeaway.tsx.
import Link from 'next/link';
import { Bot } from 'lucide-react';
import type { Post } from '@/data/posts';
import { Alert } from '@/components/common/alert';
import { ProfileAvatar } from '@/components/common/profile-avatar';
import { hasDcyfrByline, isJointByline } from '@/lib/post-credits';

export interface AIDisclosureBannerProps {
  authors?: Post['authors'];
  className?: string;
}

/**
 * AI Disclosure Banner
 *
 * Short, friendly, unambiguous notice that DCYFR — DCYFR Labs' AI lab
 * assistant — drafted or co-drafted this post, linking to /about/dcyfr.
 *
 * Operator decision D1 (2026-09-18, verbatim): "Absolutely openly an AI,
 * exactly my meaning." This banner is the disclosure that decision requires:
 * it renders on every post whose `authors` includes `dcyfr`, whether DCYFR
 * wrote it solo or alongside Drew. It never claims DCYFR is a person — the
 * copy says "AI" in the first sentence.
 *
 * Renders nothing for a Drew-solo post (no `dcyfr` in `authors`).
 *
 * @example
 * ```tsx
 * <AIDisclosureBanner authors={post.authors} />
 * ```
 */
export function AIDisclosureBanner({ authors, className }: AIDisclosureBannerProps) {
  if (!hasDcyfrByline(authors)) {
    return null;
  }

  const joint = isJointByline(authors);

  return (
    <Alert type="notice" role="note" icon={Bot} className={className}>
      <div className="flex items-center gap-3">
        <ProfileAvatar userProfile="dcyfr" size="sm" alt="DCYFR" className="hidden sm:block" />
        <p className="m-0">
          <strong>DCYFR</strong> is DCYFR Labs&rsquo; AI lab assistant &mdash; an AI, not a
          person &mdash; and {joint ? 'co-drafted this post with Drew' : 'drafted this post'}.
          Drew reviews and approves everything DCYFR publishes.{' '}
          <Link href="/about/dcyfr" className="underline hover:text-foreground">
            Meet DCYFR
          </Link>
          .
        </p>
      </div>
    </Alert>
  );
}

AIDisclosureBanner.displayName = 'AIDisclosureBanner';
